import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@root/src/environments/environment';
import { AuthService } from './auth.service';
import { TagService } from '@6grain/masofa-monolith-api-client/dictionaries/api/tag.service';

export interface TagDictionaryItem {
  id: string;
  name: string;
  description?: string;
  visible?: boolean;
  isPublic?: boolean;
  comment?: string | null;
  relationCount?: number;
}

export interface TagRelation {
  id: string;
  tagId: string;
  ownerId: string;
  ownerTypeFullName: string;
  tag?: TagDictionaryItem;
}

export interface CreateTagRequest {
  name: string;
  description?: string;
  visible?: boolean;
  isPublic?: boolean;
  comment?: string;
}

export interface CreateTagRelationRequest {
  tagId: string;
  ownerId: string;
  ownerTypeFullName: string;
}

export interface UpdateTagRequest extends CreateTagRequest {
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class TagDictionaryService {
  private readonly tagsUrl = `${environment.api}/dictionaries/tag/getall`;
  private readonly baseUrl = `${environment.api}/common`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly tagService: TagService,
  ) {}

  getAllTags(): Observable<TagDictionaryItem[]> {
    const token = this.authService.getToken();
    const headers: HttpHeaders = token
      ? new HttpHeaders({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        })
      : new HttpHeaders({
          'Content-Type': 'application/json',
        });

    return this.http.get<TagDictionaryItem[]>(this.tagsUrl, { headers }).pipe(
      map((items) => items ?? []),
      catchError(() => of([])),
    );
  }

  getVisibleTags(): Observable<TagDictionaryItem[]> {
    return this.tagService
      .dictionariesTagGetByQueryPost({
        filters: [
          {
            filterField: 'visible',
            filterValue: true,
            filterOperator: 0,
          },
        ],
      })
      .pipe(
        map(
          (items) =>
            items.map(
              (item) => ({ id: item.id, name: item.name!, description: item.description }) as TagDictionaryItem,
            ) ?? [],
        ),
        catchError(() => of([])),
      );
  }

  /**
   * Получить заголовки авторизации
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /**
   * Получить теги по владельцу
   */
  getTagsByOwner(ownerId: string, ownerTypeFullName: string): Observable<TagRelation[]> {
    const headers = this.getAuthHeaders();

    return this.http
      .get<TagDictionaryItem[]>(`${this.baseUrl}/TagRelation/GetByOwner`, {
        headers,
        params: {
          ownerId,
          ownerTypeFullName,
        },
      })
      .pipe(
        map((tags: TagDictionaryItem[]) =>
          tags.map(
            (tag) =>
              ({
                id: '',
                tagId: tag.id,
                ownerId: ownerId,
                ownerTypeFullName: ownerTypeFullName,
                tag: tag,
              }) as TagRelation,
          ),
        ),
      );
  }

  /**
   * Создать новый тег
   * Возвращает только ID созданного тега (Guid)
   */
  createTag(request: CreateTagRequest): Observable<string> {
    const headers = this.getAuthHeaders();

    return this.http.post<string>(`${this.baseUrl}/Tag/Create`, request, { headers });
  }

  createTagDetailed(request: CreateTagRequest): Observable<TagDictionaryItem> {
    return this.tagService.dictionariesTagCreatePost(request).pipe(
      map((id) => ({
        id,
        name: request.name,
        description: request.description,
        visible: request.visible ?? true,
        isPublic: request.isPublic ?? false,
        comment: request.comment,
      })),
    );
  }

  updateTag(request: UpdateTagRequest): Observable<TagDictionaryItem> {
    return this.tagService.dictionariesTagUpdatePut(request as any).pipe(
      map((tag) => ({
        id: tag.id!,
        name: tag.name || '',
        description: tag.description || '',
        visible: tag.visible,
        isPublic: tag.isPublic,
        comment: tag.comment,
      })),
    );
  }

  /**
   * Создать связь тега с объектом
   */
  createTagRelation(request: CreateTagRelationRequest): Observable<TagRelation> {
    const headers = this.getAuthHeaders();

    return this.http.post<TagRelation>(`${this.baseUrl}/TagRelation/Create`, request, { headers });
  }

  /**
   * Удалить связь тега с объектом
   */
  deleteTagRelation(tagRelationId: string): Observable<void> {
    const headers = this.getAuthHeaders();

    return this.http.delete<void>(`${this.baseUrl}/TagRelation/Delete/${tagRelationId}`, { headers });
  }

  /**
   * Удалить тег
   */
  deleteTag(tagId: string): Observable<void> {
    const headers = this.getAuthHeaders();

    return this.http.delete<void>(`${this.baseUrl}/Tag/Delete/${tagId}`, { headers });
  }

   /**
    * Add tag to satellite product (smart method - creates tag if needed)
    */
   addTagToSatelliteProduct(productId: string, tagName: string): Observable<boolean> {
     const headers = this.getAuthHeaders();

     return this.http.post<boolean>(
       `${this.baseUrl}/SatelliteProduct/AddTag/${productId}`,
       JSON.stringify(tagName), // Wrap string in JSON for [FromBody] string
       { headers },
     );
   }

   /**
    * Delete tag from satellite product
    */
  removeTagFromSatelliteProduct(productId: string, tagId: string): Observable<boolean> {
    const headers = this.getAuthHeaders();

    return this.http.delete<boolean>(`${this.baseUrl}/SatelliteProduct/RemoveTag/${productId}/${tagId}`, { headers });
  }
}
